import { memo, useContext, useRef, useEffect, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { Spin } from 'antd';
import { NoDataTableSvg } from '@/assets/svg';
import DashboardContext from '@/contexts/DashboardContext';


const NAVIGATOR_HEIGHT = 30;

const DEFAULT_MARGIN = 15;
const MEDIUM_MARGIN = 8;
const LARGE_MARGIN = 6;

const DEFAULT_EXTRA_SPACE = 1;

const MEDIUM_HEIGHT_THRESHOLD = window.innerHeight * 0.3;
const LARGE_HEIGHT_THRESHOLD = window.innerHeight * 0.5;

const ReadingsGraph = memo(
	({
		chartRef,
		graphOptions,
		isSpinnerReadingVisible,
	}: {
		chartRef: any;
		graphOptions: any;
		isSpinnerReadingVisible: boolean;
	}) => {
		const { value: context, setValue: setContext } = useContext(DashboardContext);

		const [containerHeight, setContainerHeight] = useState<number | null>(null);
		const [isUpdating, setIsUpdating] = useState(false);

		const containerRef = useRef<HTMLDivElement>(null);
		const chartContainerRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const container = containerRef.current;
			if (!container) return;

			const resizeObserver = new ResizeObserver((entries) => {
				if (!entries || !entries.length || isUpdating) return;
				const entry = entries[0];
				const newHeight = entry.contentRect.height;
				const newWidth = entry.contentRect.width;

				if (newHeight < 50 || newWidth < 50 || Math.abs(newHeight - (containerHeight || 0)) < 5) {
					return;
				}

				setContainerHeight(newHeight);
				setIsUpdating(true);

				if (chartRef.current?.chart) {
					const isExpanded = context?.isExpandedParamsCard;
					let navigatorMargin;
					let extraSpace = DEFAULT_EXTRA_SPACE;

					if (!isExpanded) {
						if (newHeight > LARGE_HEIGHT_THRESHOLD) {
							navigatorMargin = LARGE_MARGIN;
						} else if (newHeight > MEDIUM_HEIGHT_THRESHOLD) {
							navigatorMargin = MEDIUM_MARGIN;
						} else {
							navigatorMargin = DEFAULT_MARGIN;
						}
					} else {
						navigatorMargin = 50;
					}

					const controlsSpace = isExpanded ? (newHeight > LARGE_HEIGHT_THRESHOLD ? 20 : 8) : 2;
					const chartHeight = Math.max(0, newHeight - (NAVIGATOR_HEIGHT + navigatorMargin + extraSpace + controlsSpace));

					chartRef.current.chart.setSize(newWidth, chartHeight, false);

					const actualMargin = isExpanded ? 25 : 0;
					chartRef?.current?.chart.update({
						chart: {
							spacingBottom: actualMargin,
						},
						navigator: {
							enabled: true,
							height: NAVIGATOR_HEIGHT,
							margin: actualMargin,
						},
					}, true);

					setTimeout(() => setIsUpdating(false), 100);
				}
			});

			resizeObserver.observe(container);

			return () => resizeObserver.disconnect();
		}, [isUpdating, containerHeight]);

		useEffect(() => {
			let resizeTimeout: NodeJS.Timeout;

			const handleResize = () => {
				if (resizeTimeout) {
					clearTimeout(resizeTimeout);
				}

				resizeTimeout = setTimeout(() => {
					if (chartRef.current?.chart && containerRef.current) {
						const container = containerRef.current;
						const chart = chartRef.current.chart;

						const newHeight = container.clientHeight;
						const newWidth = container.clientWidth;

						if (newHeight > 50 && newWidth > 50) {
							try {
								chart.reflow();
								chart.redraw();
							} catch (error) {
								console.warn('Error resizing chart:', error);
							}
						}
					}
				}, 150);
			};

			window.addEventListener('resize', handleResize);
			window.addEventListener('orientationchange', handleResize);

			return () => {
				if (resizeTimeout) {
					clearTimeout(resizeTimeout);
				}
				window.removeEventListener('resize', handleResize);
				window.removeEventListener('orientationchange', handleResize);
			};
		}, []);

		useEffect(() => {
			if (!context?.isExpandedParamsCard) {
				const timer = setTimeout(() => {
				if (chartRef.current?.chart && containerRef.current) {
					const newHeight = containerRef.current.clientHeight;
					const newWidth = containerRef.current.clientWidth;

					if (newHeight > 50 && newWidth > 50) {
						chartRef.current.chart.reflow();
					}
				}
			}, 150);

				return () => clearTimeout(timer);
			}
		}, []);

		useEffect(() => {
			if (chartRef.current?.chart && containerHeight !== null) {
				const timer = setTimeout(() => {
					const isExpanded = context?.isExpandedParamsCard;
					let navigatorMargin;
					let extraSpace = DEFAULT_EXTRA_SPACE;

					if (!isExpanded) {
						if (containerHeight > LARGE_HEIGHT_THRESHOLD) {
							navigatorMargin = LARGE_MARGIN;
						} else if (containerHeight > MEDIUM_HEIGHT_THRESHOLD) {
							navigatorMargin = MEDIUM_MARGIN;
						} else {
							navigatorMargin = DEFAULT_MARGIN;
						}
					} else {
						navigatorMargin = 50;
					}

					const controlsSpace = isExpanded ? (containerHeight > LARGE_HEIGHT_THRESHOLD ? 20 : 8) : 2;
					const chartHeight = Math.max(100, containerHeight - (NAVIGATOR_HEIGHT + navigatorMargin + extraSpace + controlsSpace));

					const actualMargin = isExpanded ? 25 : 0;
					chartRef?.current?.chart.update({
						chart: {
							height: chartHeight,
							spacingBottom: actualMargin,
						},
						navigator: {
							enabled: true,
							height: NAVIGATOR_HEIGHT,
							margin: actualMargin,
						},
					}, true);

					setTimeout(() => setIsUpdating(false), 100);
				}, 0);
				return () => clearTimeout(timer);
			}
		}, [graphOptions, containerHeight, context?.isExpandedParamsCard]);

		return (
			<div
				ref={containerRef}
				className="flex flex-col h-full"
				style={{
					backgroundColor: 'white',
					borderRadius: '8px 0px 0px 8px',
					paddingBottom: DEFAULT_EXTRA_SPACE,
					paddingTop: '1px',
					position: 'relative',
					width: '100%',
					overflow: 'visible',
					minWidth: 0,
					minHeight: 0,
				}}
			>
				{isSpinnerReadingVisible ? (
					<div className="spinner-container self-center">
						<Spin tip="Processing data..." />
					</div>
				) : (
					<>
						{graphOptions?.series?.length ? (
							<HighchartsReact
								ref={chartRef}
								highcharts={Highcharts}
								containerProps={{
									style: {
										height: '100%',
										width: '100%',
										flex: 1,
										overflow: 'visible'
									}
								}}
								options={{
									...graphOptions,
									chart: {
										...graphOptions.chart,
										reflow: true,
										animation: false,
										style: {
											fontFamily: 'inherit',
											overflow: 'visible'
										},
										width: null,
										height: null
									},
									responsive: {
										rules: [{
											condition: {
												maxWidth: 500
											},
											chartOptions: {
												legend: {
													enabled: false
												}
											}
										}]
									}
								}}
							/>
						) : (
							<NoDataTableSvg style={{ width: '100%', height: '100%' }} />
						)}
					</>
				)}
			</div>
		);
	},
);

ReadingsGraph.displayName = 'ReadingsGraph';

export default ReadingsGraph;
